import {
  ComponentSharedPageContent,
  DynamicComponentTypes,
  PageFields,
} from '@peerless/data-access/strapi';
import { HomePageBanner } from './HomePageBanner';
import { DynamicPageBanner } from './DynamicPageBanner';
import { DynamicGrapesJsContent } from './DynamicGrapesJsContent';
import React from 'react';
import { FeaturedProductCardSection } from './FeaturedProductCardSection';
import { IconCardSection } from './IconCardSection';
import { JotForm } from './JotForm';
import { AccordionComponent } from './Accordion';
import { WYSIWYGEditor } from './WYSIWYGEditor';
import { FeaturedResourceSection } from './FeaturedResource';
import { DynamicComponentSlider } from './DynamicComponentSlider';
import { TwoColumnSection } from './TwoColumnSection';
import { ImageCardSection } from './ImageCardSection';
import { VideoSlider } from './VideoSlider';
import { VideoBannerSection } from './VideoBanner';
import { CountdownTimerSection } from './CountdownTimer';

interface Props {
  blocks: DynamicComponentTypes[] | PageFields;
  children?: React.ReactNode;
}

const renderDynamicComponent = (
  block: DynamicComponentTypes,
  index: number,
  children?: React.ReactNode
) => {
  const { __typename } = block;
  switch (__typename) {
    case 'ComponentHomePageSearchBanner':
      return <HomePageBanner key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedPageBanner':
      return <DynamicPageBanner key={`${__typename}:${index}`} block={block} />;
    case 'ComponentSharedGrapesjsContent':
      return <DynamicGrapesJsContent key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedIconCardSection':
      return <IconCardSection key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedAccordion':
      return <AccordionComponent key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedSlider':
      return <DynamicComponentSlider key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedPageContent':
      return <PageContent key={`${__typename}:${index}`}>{children}</PageContent>;
    case 'ComponentSharedJotForm':
      return <JotForm key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedFeaturedProductCardSection':
      return <FeaturedProductCardSection key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedFeaturedResource':
      return <FeaturedResourceSection key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedCkEditor':
      return <WYSIWYGEditor key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedTwoColumnSection':
      return <TwoColumnSection key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedImageCardSection':
      return <ImageCardSection key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedVideoGallery':
      return <VideoSlider key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedVideoBanner':
      return <VideoBannerSection key={`${__typename}:${index}`} data={block} />;
    case 'ComponentSharedCountdownTimer':
      return <CountdownTimerSection key={`${__typename}:${index}`} data={block} />;
    default:
      return null;
  }
};

export const DynamicBlocks: React.FC<Props> = ({ blocks, children }: Props) => {
  const data = Array.isArray(blocks) ? blocks : blocks.page.data.attributes.blocks;
  if (!data) return <PageContent>{children}</PageContent>;

  const pageContentTypeExists = data.some(
    block => block.__typename === 'ComponentSharedPageContent'
  );

  if (!pageContentTypeExists) {
    const pageContentType: ComponentSharedPageContent = {
      __typename: 'ComponentSharedPageContent',
      children: children,
    };
    data.push(pageContentType);
  }

  return data.map((block, index) => renderDynamicComponent(block, index, children));
};

interface PageContentProps {
  children?: React.ReactNode;
}
function PageContent({ children }: PageContentProps) {
  if (!children) return null;
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
